import { useState, useMemo } from 'react';
import { Calculator, TrendingUp, Download, DollarSign, BarChart3, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const SPECIALTY_OPTIONS = [
  { value: 'orthopedics', label: 'Orthopedics' },
  { value: 'spine', label: 'Spine' },
  { value: 'general', label: 'General Surgery' },
] as const;

function getLinkInvestment(monthlyVolume: number): number {
  if (monthlyVolume <= 50) return 96_000;
  if (monthlyVolume <= 150) return 150_000;
  if (monthlyVolume <= 300) return 240_000;
  return 360_000;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}

function generatePdf(data: {
  specialty: string; monthlyVolume: number; avgReimbursement: number; leakagePct: number;
  totalAnnualRevenue: number; annualLeakage: number; linkInvestment: number; netRecovery: number; roiFactor: number;
}) {
  import('jspdf').then(({ jsPDF }) => {
    const doc = new jsPDF();
    const teal = [12, 118, 111] as const;
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, 210, 297, 'F');
    doc.setTextColor(210, 220, 240);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('LINK Revenue Integrity', 20, 30);
    doc.setFontSize(14);
    doc.text('Pro Forma ROI Summary', 20, 40);
    doc.setDrawColor(...teal);
    doc.setLineWidth(0.5);
    doc.line(20, 46, 190, 46);
    doc.setFontSize(11);
    doc.setTextColor(160, 174, 192);
    doc.text('CALCULATOR INPUTS', 20, 58);
    doc.setFontSize(10);
    const inputs = [
      ['Specialty', data.specialty], ['Monthly Case Volume', String(data.monthlyVolume)],
      ['Avg. Reimbursement / Case', formatCurrency(data.avgReimbursement)], ['Estimated Current Leakage', `${data.leakagePct}%`],
    ];
    inputs.forEach(([label, val], i) => {
      doc.setTextColor(160, 174, 192); doc.text(label, 20, 68 + i * 8);
      doc.setTextColor(210, 220, 240); doc.text(val, 130, 68 + i * 8);
    });
    doc.setDrawColor(40, 50, 70); doc.line(20, 100, 190, 100);
    doc.setFontSize(11); doc.setTextColor(160, 174, 192); doc.text('PROJECTED OUTCOMES', 20, 112);
    const results = [
      ['Total Annual Earned Revenue', formatCurrency(data.totalAnnualRevenue)],
      ['Annual Revenue at Risk', formatCurrency(data.annualLeakage)],
      ['LINK Annual Investment', formatCurrency(data.linkInvestment)],
      ['Net Revenue Recovery', formatCurrency(data.netRecovery)],
      ['ROI Factor', `${data.roiFactor.toFixed(1)}x`],
    ];
    results.forEach(([label, val], i) => {
      doc.setTextColor(160, 174, 192); doc.text(label, 20, 122 + i * 10);
      const hl = i === 3 || i === 4;
      doc.setTextColor(hl ? teal[0] : 210, hl ? teal[1] : 220, hl ? teal[2] : 240);
      doc.setFont('helvetica', hl ? 'bold' : 'normal');
      doc.text(val, 130, 122 + i * 10);
      doc.setFont('helvetica', 'normal');
    });
    doc.setDrawColor(40, 50, 70); doc.line(20, 182, 190, 182);
    doc.setFontSize(7); doc.setTextColor(100, 116, 139);
    doc.text('Calculations are estimates based on LINK\'s average recovery data (13%–28%). Final results depend on specific payer mix and documentation quality.', 20, 190, { maxWidth: 170 });
    doc.text(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 20, 200);
    doc.save('LINK-ROI-ProForma.pdf');
  });
}

export function ROICalculator() {
  const [specialty, setSpecialty] = useState('orthopedics');
  const [monthlyVolume, setMonthlyVolume] = useState(100);
  const [avgReimbursement, setAvgReimbursement] = useState(15_000);
  const [leakagePct, setLeakagePct] = useState(15);

  const calculations = useMemo(() => {
    const totalAnnualRevenue = monthlyVolume * avgReimbursement * 12;
    const annualLeakage = totalAnnualRevenue * (leakagePct / 100);
    const linkInvestment = getLinkInvestment(monthlyVolume);
    const netRecovery = Math.max(0, annualLeakage - linkInvestment);
    const roiFactor = linkInvestment > 0 ? netRecovery / linkInvestment : 0;
    return { totalAnnualRevenue, annualLeakage, linkInvestment, netRecovery, roiFactor };
  }, [monthlyVolume, avgReimbursement, leakagePct]);

  const handleDownloadPdf = () => {
    const specialtyLabel = SPECIALTY_OPTIONS.find(s => s.value === specialty)?.label ?? specialty;
    generatePdf({ specialty: specialtyLabel, monthlyVolume, avgReimbursement, leakagePct, ...calculations });
  };

  return (
    <section id="roi-calculator" className="py-20 md:py-28 bg-secondary border-t border-border">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-4">
              Financial Modeling Tool
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Procedure Revenue Integrity ROI Calculator
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Model the financial impact of closing your authorization-to-billing gap.
              Adjust the inputs below to see your projected recovery.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            <Card className="lg:col-span-2 bg-card border border-border rounded-xl">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-primary" />
                  Calculator Inputs
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-7">
                <div className="space-y-2.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Specialty</Label>
                  <Select value={specialty} onValueChange={setSpecialty}>
                    <SelectTrigger className="bg-secondary border-border"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SPECIALTY_OPTIONS.map(opt => (<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Monthly Case Volume</Label>
                    <span className="text-sm font-mono font-semibold text-foreground">{monthlyVolume}</span>
                  </div>
                  <Slider value={[monthlyVolume]} onValueChange={([v]) => setMonthlyVolume(v)} min={10} max={500} step={5} className="py-1" />
                  <div className="flex justify-between text-[10px] text-muted-foreground"><span>10</span><span>500</span></div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Avg. Reimbursement / Case</Label>
                    <span className="text-sm font-mono font-semibold text-foreground">{formatCurrency(avgReimbursement)}</span>
                  </div>
                  <Slider value={[avgReimbursement]} onValueChange={([v]) => setAvgReimbursement(v)} min={2_000} max={60_000} step={500} className="py-1" />
                  <div className="flex justify-between text-[10px] text-muted-foreground"><span>$2K</span><span>$60K</span></div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Estimated Current Leakage</Label>
                    <span className="text-sm font-mono font-semibold text-primary">{leakagePct}%</span>
                  </div>
                  <Slider value={[leakagePct]} onValueChange={([v]) => setLeakagePct(v)} min={5} max={30} step={1} className="py-1" />
                  <div className="flex justify-between text-[10px] text-muted-foreground"><span>5%</span><span>30%</span></div>
                </div>
              </CardContent>
            </Card>

            <div className="lg:col-span-3 space-y-6">
              <Card className="bg-card border border-primary/20 rounded-xl relative overflow-hidden">
                <CardContent className="pt-8 pb-8 px-6 md:px-8 relative">
                  <div className="text-center mb-8">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Projected Annual Recovery</p>
                    <p className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-primary font-mono tracking-tight">
                      {formatCurrency(calculations.netRecovery)}
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-3 mb-8">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 border border-primary/20">
                      <TrendingUp className="h-4 w-4 text-primary" />
                      <span className="text-lg md:text-xl font-bold text-primary font-mono">{calculations.roiFactor.toFixed(1)}x</span>
                      <span className="text-xs text-muted-foreground font-medium">Return on Investment</span>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <MetricTile label="Total Annual Revenue" value={formatCurrency(calculations.totalAnnualRevenue)} icon={<DollarSign className="h-3.5 w-3.5" />} />
                    <MetricTile label="Revenue at Risk" value={formatCurrency(calculations.annualLeakage)} icon={<BarChart3 className="h-3.5 w-3.5" />} variant="warning" />
                    <MetricTile label="LINK Investment" value={formatCurrency(calculations.linkInvestment)} icon={<ShieldCheck className="h-3.5 w-3.5" />} />
                  </div>
                </CardContent>
              </Card>
              <Button onClick={handleDownloadPdf} className="w-full h-12 text-sm font-semibold gap-2" variant="outline">
                <Download className="h-4 w-4" />
                Download Pro Forma PDF
              </Button>
            </div>
          </div>

          <div className="mt-10 md:mt-14">
            <p className="flex items-start gap-2 text-[11px] text-muted-foreground text-center justify-center max-w-3xl mx-auto leading-relaxed">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" strokeWidth={1.5} />
              Calculations are estimates based on LINK's average recovery data (13%–28%). Final results depend on specific payer mix and documentation quality. No PHI or hospital data is stored.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function MetricTile({ label, value, icon, variant = 'default' }: { label: string; value: string; icon: React.ReactNode; variant?: 'default' | 'warning' }) {
  return (
    <div className="rounded-lg bg-secondary border border-border p-4 text-center">
      <div className={`inline-flex items-center gap-1.5 mb-1.5 ${variant === 'warning' ? 'text-warning' : 'text-muted-foreground'}`}>
        {icon}
        <span className="text-[10px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-base md:text-lg font-bold font-mono text-foreground">{value}</p>
    </div>
  );
}
